import dedent from 'dedent';
import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'read-file' as const;

const argumentZod = z.object({
  path: z
    .string()
    .describe(
      'The virtual file path to read, as returned by the file listing tool. Examples: site/components/hero-section-abc123.wfdl, site/builtin/Section.wfdl, site/styles.css'
    ),
  startLine: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe(
      'Line number to start reading from (1-indexed). If omitted, reads from the beginning.'
    ),
  endLine: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe(
      'Line number to stop reading at (inclusive). If omitted, reads to the end or until the max output limit is reached.'
    ),
});

export const readFileTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Read file',
  prettyStatus: {
    executing: 'Reading file...',
    executed: 'Read file',
    failed: 'Failed to read file',
  },
  prettyErrors: {
    fallback: 'Unable to read file.',
    patterns: [
      {
        match: 'not found',
        message: 'File not found.',
      },
      {
        match: 'not a supported file',
        message: 'File is not a supported file.',
      },
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Reads a file from the site's virtual file system. Use the file listing tool to discover available files and their paths.

      There is no page file. Pages are read with \`read-page\`; \`read-file\` only serves components, builtin elements, and \`site/styles.css\`. Never construct a page path.

      Supported file types:
      - **Components** (site/components/<name>-<id>.wfdl): Returns the component's WFDL (Webflow Design Language) source code, which describes the component's element tree, structure, and properties.
      - **Builtin Elements** (site/builtin/<Name>.wfdl): Returns HTML template and usage guidance for a builtin element type (e.g. Section, NavbarWrapper, SliderWrapper). Use this to get the correct HTML structure for the render tool.
      - **Styles** (site/styles.css): Returns all CSS styles defined on the site as a CSS file, with selectors and their properties. Variable references are resolved to var(--name) syntax.

      The file path format for components is <name>-<id>.<extension> where the ID is the last hyphen-delimited segment before the file extension. Component names may contain hyphens.

      Returns the file content as structured data including source and metadata. Large files may be truncated — use startLine and endLine to read specific sections.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [{resource: 'SiteSymbol', action: 'read'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
  timeout: 15000,
});
